<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
    CalendarRange,
    CheckCircle2,
    Clock3,
    FileText,
    Loader2,
    MessageCircleMore,
    Paperclip,
    Share2,
    Trash2,
} from 'lucide-vue-next';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import TaskAssigneePicker from '@/components/tasks/TaskAssigneePicker.vue';
import TaskMilestoneSelect from '@/components/tasks/TaskMilestoneSelect.vue';
import TagInput from '@/components/entity/TagInput.vue';
import TaskOptionMenu from '@/components/tasks/TaskOptionMenu.vue';
import TimerControl from '@/components/time/TimerControl.vue';
import TaskCommentsPanel from '@/components/tasks/details/TaskCommentsPanel.vue';
import TaskUploadsPanel from '@/components/tasks/details/TaskUploadsPanel.vue';
import TaskDescriptionPanel from '@/components/tasks/details/TaskDescriptionPanel.vue';
import TaskSchedulePanel from '@/components/tasks/details/TaskSchedulePanel.vue';
import { normalizeTaskScheduleDates } from '~/lib/taskSchedule';
import TaskSharingPanel from '@/components/tasks/details/TaskSharingPanel.vue';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';
import SheetSectionNavigation from '@/components/editing/SheetSectionNavigation.vue';
import type { ProjectStage, Task, TaskPriority, TenantTaskStatusOption } from '~/types/models';
import { buildTaskAssigneePayload, resolveTaskAssigneeIds } from '~/lib/taskAssignees';
import { getTaskStatusOption } from '~/lib/taskConfig';
import { toast } from 'vue-sonner';

interface Props {
    open: boolean;
    taskId?: string | null;
    statusOptions?: string[];
    statusOptionDetails?: TenantTaskStatusOption[];
    assigneeOptions?: Array<{ id: string; label: string; avatarURL?: string }>;
    priorityOptions?: Array<{ value: TaskPriority; label: string; color?: string }>;
    projectOptions?: Array<{ id: string; name: string; clientId?: string; stages?: ProjectStage[] }>;
    clientOptions?: Array<{ id: string; name: string }>;
}

const props = withDefaults(defineProps<Props>(), {
    statusOptions: () => ['Todo', 'In Progress', 'Blocked', 'Done'],
    statusOptionDetails: () => [],
    assigneeOptions: () => [],
    priorityOptions: () => [
        { value: 'low', label: 'Low', color: '#2563eb' },
        { value: 'medium', label: 'Medium', color: '#d97706' },
        { value: 'high', label: 'High', color: '#ea580c' },
        { value: 'critical', label: 'Critical', color: '#dc2626' },
    ],
    projectOptions: () => [],
    clientOptions: () => [],
});

const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'task-updated', task: Task): void;
    (e: 'task-deleted', taskId: string): void;
}>();

const taskStore = useTaskStore();
const entityTagsStore = useEntityTagsStore();
const timeEntriesStore = useTimeEntriesStore();
const tenantStore = useTenantStore();
const notificationRead = useNotificationReadAcknowledger();
const subscription = useWorkspaceSubscription();
const { canMutate } = usePermissions();
type AutosaveTextField = 'title' | 'description' | 'clientResponsePrompt';

const loading = ref(false);
const error = ref<string | null>(null);
const task = ref<Task | null>(null);
const editableTask = ref<Partial<Task>>({});
const detailPanel = ref<'details' | 'schedule' | 'uploads' | 'notes' | 'sharing'>('details');
const saveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle');
const saveStateTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const textAutosaveTimers = new Map<AutosaveTextField, ReturnType<typeof setTimeout>>();
const tenantFollowUpMessage = ref('');
const requestingFollowUp = ref(false);
const deleteConfirmOpen = ref(false);
const deleting = ref(false);
const detailPanelOptions = [
    { value: 'details', label: 'Details', icon: FileText },
    { value: 'schedule', label: 'Schedule', icon: CalendarRange },
    { value: 'uploads', label: 'Uploads', icon: Paperclip },
    { value: 'notes', label: 'Notes/Comments', icon: MessageCircleMore },
    { value: 'sharing', label: 'Sharing', icon: Share2 },
] as const;

const taskDescription = computed({
    get: () => editableTask.value.description || '',
    set: (value: string) => {
        editableTask.value.description = value;
    },
});

const doneStatuses = new Set(['done', 'completed']);

const isCompleted = computed(() => doneStatuses.has(String(editableTask.value.status || '').toLowerCase()));
const { tenantPath } = useTenantPath();
const linkedProjectHref = computed(() =>
    editableTask.value.projectId
        ? tenantPath(`/projects/${editableTask.value.projectId}`)
        : '',
);
const saveStateLabel = computed(() => {
    if (saveState.value === 'saving') return 'Saving changes...';
    if (saveState.value === 'saved') return 'Saved just now';
    if (saveState.value === 'error') return 'Could not save';
    return 'All changes sync automatically';
});
const updatedSummary = computed(() => formatTimestamp(task.value?.updatedAt));
const dueDateSummary = computed(() => formatShortDate(editableTask.value.dueDate));
const reminderDateValue = computed(() => toDateOnlyValue(editableTask.value.reminderAt));
const reminderSummary = computed(() => formatDateValue(editableTask.value.reminderAt));
const scheduleStartValue = computed(() => editableTask.value.startDate || editableTask.value.dueDate || '');
const scheduleEndValue = computed(() => (
    editableTask.value.startDate && editableTask.value.endDate ? editableTask.value.endDate : ''
));
const recurringSummary = computed(() => {
    const recurring = editableTask.value.recurring;
    if (!recurring?.enabled) return 'Does not repeat';

    switch (recurring.frequency) {
        case 'weekly':
            return recurring.daysOfWeek?.length
                ? `Repeats weekly on ${recurring.daysOfWeek.length} day${recurring.daysOfWeek.length === 1 ? '' : 's'}`
                : 'Repeats weekly';
        case 'monthly':
            return recurring.dayOfMonth ? `Repeats monthly on day ${recurring.dayOfMonth}` : 'Repeats monthly';
        case 'yearly':
            return 'Repeats yearly';
        default:
            return 'Repeats daily';
    }
});
const projectOptionMap = computed(() => new Map(
    props.projectOptions.map(project => [project.id, project]),
));
const projectMenuOptions = computed(() => props.projectOptions.map(project => ({
    value: project.id,
    label: project.name,
})));
const clientMenuOptions = computed(() => props.clientOptions.map(client => ({
    value: client.id,
    label: client.name,
})));
const selectedProjectOption = computed(() => {
    const projectId = String(editableTask.value.projectId || '').trim();
    return projectId ? projectOptionMap.value.get(projectId) || null : null;
});
const resolvedClientSelectValue = computed(() => (
    String(editableTask.value.clientId || selectedProjectOption.value?.clientId || '')
));
const scheduleSummary = computed(() => {
    const start = editableTask.value.startDate;
    const end = editableTask.value.endDate;
    const due = editableTask.value.dueDate;

    if (start && end) {
        return `Scheduled from ${formatShortDate(start)} to ${formatShortDate(end)}`;
    }

    if (due) {
        return `Due ${formatShortDate(due)}`;
    }

    return 'No schedule yet';
});
const selectedAssigneeIds = computed(() => resolveTaskAssigneeIds(editableTask.value));
const taskTags = computed(() => props.taskId ? entityTagsStore.tagsFor('task', props.taskId) : []);
const tagSuggestions = computed(() => entityTagsStore.allTags);
const statusMenuOptions = computed(() => (
    props.statusOptionDetails?.length
        ? props.statusOptionDetails
        : (props.statusOptions || []).map(option => ({ value: option, label: option, color: '#64748b' }))
));
const selectedStatusStyle = computed(() => {
    const option = getTaskStatusOption(String(editableTask.value.status || ''), props.statusOptionDetails || []);
    if (!option?.color) return undefined;

    return {
        borderColor: `color-mix(in srgb, ${option.color} 34%, transparent)`,
        backgroundColor: `color-mix(in srgb, ${option.color} 14%, transparent)`,
        color: option.color,
    };
});
const selectedPriorityDisplay = computed(() => {
    const value = String(editableTask.value.priority || '');
    const option = props.priorityOptions.find(entry => entry.value === value);
    return {
        label: option?.label || 'No priority',
        color: option?.color || '#94a3b8',
    };
});
const clientResponseThread = computed(() => (
    Array.isArray(editableTask.value.clientResponseThread) ? editableTask.value.clientResponseThread : []
));

watch(() => [props.open, props.taskId], ([isOpen, id]) => {
    if (isOpen && id) {
        void loadTask();
        notificationRead.acknowledgeEntity('task', id, () => ({
            surface: 'tenant',
            tenantId: tenantStore.currentTenantId,
        }));
    } else if (!isOpen) {
        clearAllTextAutosaveTimers();
        task.value = null;
        editableTask.value = {};
        error.value = null;
        saveState.value = 'idle';
    }
}, { immediate: true });

watch(() => props.open, (isOpen) => {
    if (isOpen) {
        detailPanel.value = 'details';
    }
});

onBeforeUnmount(() => {
    if (saveStateTimer.value) {
        clearTimeout(saveStateTimer.value);
    }
    clearAllTextAutosaveTimers();
});

function setSaveState(nextState: 'idle' | 'saving' | 'saved' | 'error') {
    saveState.value = nextState;

    if (saveStateTimer.value) {
        clearTimeout(saveStateTimer.value);
        saveStateTimer.value = null;
    }

    if (nextState === 'saved') {
        saveStateTimer.value = setTimeout(() => {
            saveState.value = 'idle';
        }, 2200);
    }
}

async function loadTask() {
    if (!props.taskId) return;

    loading.value = true;
    error.value = null;

    try {
        const [fetchedTask] = await Promise.all([
            taskStore.fetchTaskById(props.taskId),
            entityTagsStore.fetchEntityTags(),
            timeEntriesStore.fetchRunning().catch((timerError) => {
                console.warn('Unable to restore the active timer:', timerError);
            }),
        ]);

        if (!fetchedTask) {
            throw new Error('Task not found');
        }

        task.value = fetchedTask;
        editableTask.value = { ...fetchedTask };
        clearAllTextAutosaveTimers();
        setSaveState('idle');
    } catch (loadError: any) {
        error.value = loadError?.message || 'Failed to load task';
        console.error('Error loading task:', loadError);
    } finally {
        loading.value = false;
    }
}

function clearTextAutosaveTimer(field: AutosaveTextField) {
    const timer = textAutosaveTimers.get(field);
    if (timer) {
        clearTimeout(timer);
        textAutosaveTimers.delete(field);
    }
}

function clearAllTextAutosaveTimers() {
    for (const timer of textAutosaveTimers.values()) {
        clearTimeout(timer);
    }
    textAutosaveTimers.clear();
}

type TextAutosaveOptions = {
    delay?: number;
    required?: boolean;
    emptyAsUndefined?: boolean;
    trim?: boolean;
};

function getNormalizedTextAutosaveValue(rawValue: string, options: TextAutosaveOptions) {
    if (options.required) {
        return options.trim === false ? rawValue : rawValue.trim();
    }

    if (options.emptyAsUndefined) {
        return rawValue.trim() ? rawValue : undefined;
    }

    if (options.trim === false) {
        return rawValue;
    }

    const trimmed = rawValue.trim();
    return trimmed || undefined;
}

async function persistTextAutosaveField(field: AutosaveTextField, options: TextAutosaveOptions = {}) {
    clearTextAutosaveTimer(field);

    if (!props.taskId || !task.value) return;

    const rawValue = String(editableTask.value[field] || '');
    const normalizedValue = getNormalizedTextAutosaveValue(rawValue, options);

    if (options.required && !normalizedValue) {
        return;
    }

    const currentValue = String(task.value[field] || '');
    const comparableValue = normalizedValue === undefined ? '' : String(normalizedValue);
    if (currentValue === comparableValue) {
        return;
    }

    const originalValue = task.value[field];
    task.value = { ...task.value, [field]: normalizedValue };
    setSaveState('saving');

    try {
        await taskStore.updateTask(props.taskId, { [field]: normalizedValue } as Partial<Task>);
        emit('task-updated', task.value);
        setSaveState('saved');

        if (String(editableTask.value[field] || '') !== rawValue) {
            scheduleTextAutosaveField(field, { ...options, delay: 250 });
        }
    } catch (saveError: any) {
        task.value = { ...task.value, [field]: originalValue };
        (editableTask.value as Record<string, unknown>)[field] = String(originalValue || '');
        error.value = `Failed to update ${field}`;
        setSaveState('error');
        console.error(`Error updating task ${field}:`, saveError);
    }
}

function scheduleTextAutosaveField(field: AutosaveTextField, options: TextAutosaveOptions = {}) {
    if (!props.open || !props.taskId || !task.value || loading.value) {
        return;
    }

    clearTextAutosaveTimer(field);
    const timer = setTimeout(() => {
        void persistTextAutosaveField(field, options);
    }, options.delay ?? 650);
    textAutosaveTimers.set(field, timer);
}

async function flushTextAutosaveField(field: AutosaveTextField, options: TextAutosaveOptions = {}) {
    await persistTextAutosaveField(field, options);
}

async function saveField(field: keyof Task) {
    if (!props.taskId || !task.value) return;

    const originalValue = task.value[field];
    const newValue = editableTask.value[field];

    task.value = { ...task.value, [field]: newValue };
    setSaveState('saving');

    try {
        const updates: Record<string, any> = { [field]: newValue };

        if (field === 'assigneeId' || field === 'assigneeIds') {
            const assigneeIds = field === 'assigneeIds'
                ? resolveTaskAssigneeIds({ assigneeIds: Array.isArray(newValue) ? newValue : [] })
                : (String(newValue || '').trim() ? [String(newValue)] : []);
            Object.assign(updates, buildTaskAssigneePayload(assigneeIds, props.assigneeOptions));
            Object.assign(editableTask.value, updates);
        }

        await taskStore.updateTask(props.taskId, updates);
        emit('task-updated', task.value);
        setSaveState('saved');
    } catch (saveError: any) {
        task.value = { ...task.value, [field]: originalValue };
        (editableTask.value as Record<string, unknown>)[field] = originalValue;
        error.value = `Failed to update ${field}`;
        setSaveState('error');
        console.error('Error updating task:', saveError);
    }
}

async function saveTaskFields(fields: Array<keyof Task>) {
    if (!props.taskId || !task.value) return;

    const originalValues = Object.fromEntries(fields.map(field => [field, task.value?.[field]])) as Partial<Task>;
    const updates = Object.fromEntries(fields.map(field => [field, editableTask.value[field]])) as Partial<Task>;

    task.value = { ...task.value, ...updates };
    setSaveState('saving');

    try {
        await taskStore.updateTask(props.taskId, updates);
        emit('task-updated', task.value);
        setSaveState('saved');
    } catch (saveError: any) {
        task.value = { ...task.value, ...originalValues };
        for (const field of fields) {
            (editableTask.value as Record<string, unknown>)[field] = originalValues[field];
        }
        error.value = 'Failed to update schedule';
        setSaveState('error');
        console.error('Error updating task fields:', saveError);
    }
}

async function saveRecurring(recurringConfig: NonNullable<Task['recurring']>) {
    if (!props.taskId || !task.value) return;
    if (!subscription.canUseRecurringTasks.value) {
        error.value = 'Recurring tasks are available on the Growth plan and above.';
        setSaveState('error');
        return;
    }

    const originalValue = task.value.recurring;
    setSaveState('saving');

    try {
        task.value.recurring = recurringConfig;
        await taskStore.updateTask(props.taskId, { recurring: recurringConfig });
        emit('task-updated', task.value);
        setSaveState('saved');
    } catch (saveError: any) {
        task.value.recurring = originalValue;
        editableTask.value.recurring = originalValue;
        error.value = 'Failed to save recurring configuration';
        setSaveState('error');
        console.error('Error saving recurring config:', saveError);
    }
}

async function handleRecurringEnabledToggle(value: boolean) {
    const current = editableTask.value.recurring;
    const nextRecurring: NonNullable<Task['recurring']> = {
        enabled: value,
        frequency: current?.frequency || 'monthly',
        endDate: current?.endDate,
        daysOfWeek: current?.daysOfWeek?.length ? [...current.daysOfWeek] : [1, 2, 3, 4, 5],
        dayOfMonth: current?.dayOfMonth || 1,
    };

    editableTask.value.recurring = nextRecurring;

    await saveRecurring(nextRecurring);
}

async function saveReminderDate(value: string) {
    editableTask.value.reminderAt = value ? new Date(`${value}T09:00:00`).toISOString() : '';
    await saveField('reminderAt');
}

async function updateSchedule(startValue?: string, endValue?: string) {
    const normalized = normalizeTaskScheduleDates(startValue, endValue);
    editableTask.value.dueDate = normalized.dueDate || '';
    editableTask.value.startDate = normalized.startDate || '';
    editableTask.value.endDate = normalized.endDate || '';

    await saveTaskFields(['dueDate', 'startDate', 'endDate']);
}

async function handleScheduleStartChange(value: string) {
    await updateSchedule(value, scheduleEndValue.value);
}

async function handleScheduleEndChange(value: string) {
    await updateSchedule(scheduleStartValue.value, value);
}

async function handleClientVisibilityToggle(value: boolean) {
    editableTask.value.clientVisible = value;
    if (editableTask.value.clientVisible !== true) {
        editableTask.value.clientResponseRequired = false;
    }

    await saveTaskFields(['clientVisible', 'clientResponseRequired']);
}

async function handleProjectSelection(value: string) {
    const nextProjectId = String(value || '');
    const projectChanged = nextProjectId !== String(editableTask.value.projectId || '');
    editableTask.value.projectId = nextProjectId;
    if (projectChanged) editableTask.value.milestoneId = '';

    const derivedClientId = nextProjectId
        ? String(projectOptionMap.value.get(nextProjectId)?.clientId || '').trim()
        : '';

    if (derivedClientId) {
        editableTask.value.clientId = derivedClientId;
    }

    const fields: Array<keyof Task> = ['projectId'];
    if (projectChanged) fields.push('milestoneId');
    if (derivedClientId && editableTask.value.clientId === derivedClientId) {
        fields.push('clientId');
    }

    await saveTaskFields(fields);
}

async function handleMilestoneSelection(value: string) {
    editableTask.value.milestoneId = String(value || '');
    await saveField('milestoneId');
}

async function handleClientSelection(value: string) {
    editableTask.value.clientId = String(value || '');

    await saveField('clientId');
}

async function handleAssigneeSelection(value: string[]) {
    Object.assign(editableTask.value, buildTaskAssigneePayload(value, props.assigneeOptions));

    await saveTaskFields(['assigneeIds', 'assignees', 'assigneeId', 'assignee']);
}

async function handleTagsUpdate(tags: string[]) {
    if (!props.taskId) return;
    setSaveState('saving');
    try {
        await entityTagsStore.setEntityTags('task', props.taskId, tags);
        setSaveState('saved');
    } catch (saveError: any) {
        error.value = saveError?.message || 'Failed to save tags';
        setSaveState('error');
    }
}

async function handleClientResponseRequiredToggle(value: boolean) {
    editableTask.value.clientResponseRequired = value;
    if (value) {
        editableTask.value.clientVisible = true;
        if (!editableTask.value.clientResponseStatus || editableTask.value.clientResponseStatus === 'reviewed') {
            editableTask.value.clientResponseStatus = 'waiting';
        }
    }
    else if (editableTask.value.clientResponseStatus === 'waiting') {
        editableTask.value.clientResponseStatus = undefined;
    }

    await saveTaskFields(['clientVisible', 'clientResponseRequired', 'clientResponseStatus']);
}

async function sendClientFollowUpRequest() {
    if (!props.taskId) return;
    const message = tenantFollowUpMessage.value.trim();
    if (!message) return;

    requestingFollowUp.value = true;
    setSaveState('saving');

    try {
        const updatedTask = await taskStore.requestClientTaskFollowUp(props.taskId, message);
        if (updatedTask) {
            editableTask.value = {
                ...editableTask.value,
                ...updatedTask,
            };
            if (task.value) {
                task.value = {
                    ...task.value,
                    ...updatedTask,
                };
            } else {
                task.value = updatedTask;
            }
        }
        tenantFollowUpMessage.value = '';
        setSaveState('saved');
    } catch (followUpError: any) {
        error.value = followUpError?.message || 'Failed to request more information from the client.';
        setSaveState('error');
    } finally {
        requestingFollowUp.value = false;
    }
}

async function markTaskDone() {
    const doneStatus = props.statusOptions.find((status) => doneStatuses.has(status.toLowerCase())) || 'Done';
    editableTask.value.status = doneStatus;
    await saveField('status');
}

async function startTaskTimer() {
    if (!task.value) return;
    const project = props.projectOptions.find(option => option.id === task.value?.projectId);
    const resolvedClientId = task.value.clientId || project?.clientId;
    const client = props.clientOptions.find(option => option.id === resolvedClientId);
    try {
        await timeEntriesStore.startTimer({
            taskId: task.value.id,
            taskTitle: task.value.title,
            projectId: project?.id,
            projectName: project?.name,
            clientId: client?.id,
            clientName: client?.name,
        });
        toast.success('Timer started for this task.');
    } catch (timerError: any) {
        toast.error(timerError?.message || 'Unable to start the timer.');
    }
}

async function stopTaskTimer(entryId: string) {
    try {
        await timeEntriesStore.stopTimer(entryId);
        toast.success('Time entry saved.');
    } catch (timerError: any) {
        toast.error(timerError?.message || 'Unable to stop the timer.');
    }
}

async function pauseTaskTimer(entryId: string) {
    try {
        await timeEntriesStore.pauseTimer(entryId);
        toast.success('Timer paused.');
    } catch (timerError: any) {
        toast.error(timerError?.message || 'Unable to pause the timer.');
    }
}

async function resumeTaskTimer(entryId: string) {
    try {
        await timeEntriesStore.resumeTimer(entryId);
        toast.success('Timer resumed.');
    } catch (timerError: any) {
        toast.error(timerError?.message || 'Unable to resume the timer.');
    }
}

async function handleDelete() {
    if (!props.taskId || !task.value) return;
    deleting.value = true;
    try {
        await taskStore.deleteTask(props.taskId);
        emit('task-deleted', props.taskId);
        deleteConfirmOpen.value = false;
        handleOpenChange(false);
    } catch (deleteError: any) {
        error.value = deleteError?.message || 'Failed to delete task';
        setSaveState('error');
        console.error('Error deleting task:', deleteError);
    } finally {
        deleting.value = false;
    }
}

function handleOpenChange(value: boolean) {
    if (!value) {
        void Promise.all([
            flushTextAutosaveField('title', { trim: true, required: true }),
            flushTextAutosaveField('description', { emptyAsUndefined: true, trim: false }),
            flushTextAutosaveField('clientResponsePrompt', { emptyAsUndefined: true, trim: false }),
        ]);
    }
    emit('update:open', value);
}

function formatTimestamp(timestamp: unknown): string {
    if (!timestamp) return 'Unknown';

    try {
        const date = typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp
            ? (timestamp as { toDate: () => Date }).toDate()
            : new Date(String(timestamp));

        if (Number.isNaN(date.getTime())) {
            return 'Unknown';
        }

        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return 'Unknown';
    }
}

function formatShortDate(value?: string): string {
    if (!value) return 'Not set';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not set';

    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function formatDateValue(value?: string): string {
    if (!value) return 'Not set';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Not set';

    return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function toDateOnlyValue(value?: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

watch(() => editableTask.value.title, (value, previousValue) => {
    if (value === previousValue) return;
    scheduleTextAutosaveField('title', { trim: true, required: true });
});

watch(() => editableTask.value.description, (value, previousValue) => {
    if (value === previousValue) return;
    scheduleTextAutosaveField('description', { emptyAsUndefined: true, trim: false });
});

watch(() => editableTask.value.clientResponsePrompt, (value, previousValue) => {
    if (value === previousValue) return;
    scheduleTextAutosaveField('clientResponsePrompt', { emptyAsUndefined: true, trim: false });
});

</script>

<template>
    <Sheet :open="open" @update:open="handleOpenChange">
        <SheetContent
            side="right"
            class="!flex h-full !w-full max-w-none flex-col gap-0 overflow-hidden bg-background p-0 sm:!max-w-[800px]"
        >
            <SheetTitle class="sr-only">Task details</SheetTitle>
            <SheetDescription class="sr-only">View and edit task details, schedule, files, comments, and client sharing.</SheetDescription>
            <div v-if="loading" class="flex h-full items-center justify-center gap-3 text-muted-foreground">
                <Loader2 class="size-5 animate-spin" />
                <p>Loading task details...</p>
            </div>

            <div v-else-if="error && !task" class="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
                <p class="max-w-md text-sm text-destructive">{{ error }}</p>
                <Button @click="loadTask" variant="outline">Try Again</Button>
            </div>

            <div
                v-else-if="task"
                class="relative flex h-full min-h-0 flex-1 flex-col gap-0"
            >
                <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    <SheetHeader class="gap-5 border-b border-border/70 px-5 py-5 pr-14 text-left sm:px-6 sm:pr-14">
                        <div class="flex min-w-0 items-start justify-between gap-4">
                            <div class="min-w-0 flex-1">
                                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Task workspace</p>
                                <Input
                                    v-model="editableTask.title"
                                    placeholder="What needs to get done?"
                                    class="mt-1 h-auto min-h-0 border-0 bg-transparent px-0 py-1 text-2xl font-semibold tracking-tight text-foreground shadow-none focus-visible:ring-0 sm:text-3xl"
                                    @blur="flushTextAutosaveField('title', { trim: true, required: true })"
                                />
                                <SheetDescription class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                    <span class="inline-flex items-center gap-1.5">
                                        <CalendarRange class="size-3.5" />
                                        {{ scheduleSummary }}
                                    </span>
                                    <span class="inline-flex items-center gap-1.5">
                                        <Clock3 class="size-3.5" />
                                        Updated {{ updatedSummary }}
                                    </span>
                                </SheetDescription>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                class="mt-5 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                @click="deleteConfirmOpen = true"
                            >
                                <Trash2 class="size-4" />
                                <span class="sr-only">Delete task</span>
                            </Button>
                        </div>

                        <div class="grid min-w-0 grid-cols-3 items-end gap-4">
                            <div class="min-w-0">
                                <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Status</label>
                                <TaskOptionMenu
                                    :model-value="String(editableTask.status || '')"
                                    type="status"
                                    :options="statusMenuOptions"
                                    trigger-class="mt-2 h-9 w-full rounded-md border border-border bg-background px-3 shadow-none"
                                    @update:model-value="(value) => { editableTask.status = value; saveField('status'); }"
                                >
                                    <template #trigger="{ option }">
                                        <div class="flex min-w-0 items-center gap-2" :style="selectedStatusStyle">
                                            <span class="size-2 rounded-full" :style="{ backgroundColor: option?.color || selectedStatusStyle?.color || '#64748b' }" />
                                            <span class="truncate text-sm font-medium">{{ option?.label || 'Select status' }}</span>
                                        </div>
                                    </template>
                                </TaskOptionMenu>
                            </div>

                            <div class="min-w-0">
                                <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Priority</label>
                                <TaskOptionMenu
                                    :model-value="String(editableTask.priority || '')"
                                    type="priority"
                                    :options="priorityOptions"
                                    allow-clear
                                    clear-label="No priority"
                                    trigger-class="mt-2 h-9 w-full rounded-md border border-border bg-background px-3 shadow-none"
                                    @update:model-value="(value) => { editableTask.priority = value ? (value as TaskPriority) : undefined; saveField('priority'); }"
                                >
                                    <template #trigger="{ option }">
                                        <div class="flex min-w-0 items-center gap-2">
                                            <span class="size-2 rounded-full" :style="{ backgroundColor: option?.color || selectedPriorityDisplay.color }" />
                                            <span class="truncate text-sm font-medium" :style="{ color: option?.color || selectedPriorityDisplay.color }">
                                                {{ option?.label || selectedPriorityDisplay.label }}
                                            </span>
                                        </div>
                                    </template>
                                </TaskOptionMenu>
                            </div>

                            <div class="min-w-0">
                                <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Assignee</label>
                                <TaskAssigneePicker
                                    :model-value="selectedAssigneeIds"
                                    :options="assigneeOptions"
                                    avatar-only-trigger
                                    :show-chevron="false"
                                    trigger-class="mt-2"
                                    @update:model-value="handleAssigneeSelection"
                                />
                            </div>
                        </div>

                        <div class="grid gap-4 border-t border-border/60 pt-5 sm:grid-cols-3">
                            <div class="min-w-0">
                                <label class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Project</label>
                                <SearchableSelectMenu
                                    :model-value="String(editableTask.projectId || '')"
                                    :options="projectMenuOptions"
                                    placeholder="No linked project"
                                    search-placeholder="Search projects..."
                                    allow-clear
                                    clear-label="No project"
                                    show-all-by-default
                                    trigger-class="mt-2 flex h-9 w-full items-center rounded-md border border-border bg-background px-3 text-left shadow-none"
                                    @update:model-value="handleProjectSelection"
                                />
                            </div>
                            <TaskMilestoneSelect
                                :model-value="String(editableTask.milestoneId || '')"
                                :project-id="String(editableTask.projectId || '')"
                                :project-options="projectOptions"
                                label="Milestone"
                                label-class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
                                trigger-class="mt-2 flex h-9 w-full items-center rounded-md border border-border bg-background px-3 text-left shadow-none"
                                storage-key="task-details-milestone"
                                @update:model-value="handleMilestoneSelection"
                            />
                            <div class="min-w-0">
                                <label class="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Client</label>
                                <SearchableSelectMenu
                                    :model-value="resolvedClientSelectValue"
                                    :options="clientMenuOptions"
                                    placeholder="No linked client"
                                    search-placeholder="Search clients..."
                                    allow-clear
                                    clear-label="No client"
                                    show-all-by-default
                                    trigger-class="mt-2 flex h-9 w-full items-center rounded-md border border-border bg-background px-3 text-left shadow-none"
                                    @update:model-value="handleClientSelection"
                                />
                            </div>
                        </div>

                        <div class="grid min-w-0 gap-4 sm:grid-cols-2">
                            <div class="min-w-0">
                                <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Start or deadline</label>
                                <DatePicker
                                    :model-value="scheduleStartValue"
                                    placeholder="Select date"
                                    :show-icon="false"
                                    class="mt-2 h-9 min-w-0 w-full"
                                    @update:model-value="handleScheduleStartChange"
                                />
                            </div>
                            <div class="min-w-0">
                                <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">End date</label>
                                <DatePicker
                                    :model-value="scheduleEndValue"
                                    placeholder="Optional"
                                    :show-icon="false"
                                    class="mt-2 h-9 min-w-0 w-full"
                                    @update:model-value="handleScheduleEndChange"
                                />
                            </div>
                        </div>

                        <div class="border-t border-border/60 pt-5">
                            <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Tags</label>
                            <div class="mt-2">
                                <TagInput
                                    :model-value="taskTags"
                                    :suggestions="tagSuggestions"
                                    placeholder="Add an internal task tag"
                                    @update:model-value="handleTagsUpdate"
                                />
                            </div>
                        </div>
                    </SheetHeader>

                    <div class="min-w-0 px-5 py-5 sm:px-6">
                        <div class="min-w-0 space-y-4">
                            <template v-if="detailPanel === 'details'">
                                <TaskDescriptionPanel
                                    v-model="taskDescription"
                                    @save="flushTextAutosaveField('description', { emptyAsUndefined: true, trim: false })"
                                />
                            </template>

                            <template v-else-if="detailPanel === 'schedule'">
                                <TaskSchedulePanel
                                    :schedule-summary="scheduleSummary"
                                    :reminder-summary="reminderSummary"
                                    :recurring-summary="recurringSummary"
                                    :reminder-date="reminderDateValue"
                                    :recurring="editableTask.recurring"
                                    :can-use-recurring-tasks="subscription.canUseRecurringTasks.value"
                                    :recurring-requirement="subscription.requiredLabelFor('recurringTasks')"
                                    @update:reminder="saveReminderDate"
                                    @toggle:recurring="handleRecurringEnabledToggle"
                                    @update:recurring="saveRecurring"
                                />
                            </template>

                            <template v-else-if="detailPanel === 'uploads'">
                                <TaskUploadsPanel v-if="task.id" :task-id="task.id" />
                            </template>

                            <template v-else-if="detailPanel === 'notes'">
                                <TaskCommentsPanel v-if="task.id" :task-id="task.id" />
                            </template>

                            <template v-else>
                                <TaskSharingPanel
                                    :client-visible="editableTask.clientVisible === true"
                                    :response-required="editableTask.clientResponseRequired === true"
                                    :response-prompt="editableTask.clientResponsePrompt || ''"
                                    :response-status="editableTask.clientResponseStatus || 'waiting'"
                                    :response-thread="clientResponseThread"
                                    :follow-up-message="tenantFollowUpMessage"
                                    :requesting-follow-up="requestingFollowUp"
                                    @update:client-visible="handleClientVisibilityToggle"
                                    @update:response-required="handleClientResponseRequiredToggle"
                                    @update:response-prompt="editableTask.clientResponsePrompt = $event"
                                    @save:response-prompt="flushTextAutosaveField('clientResponsePrompt', { emptyAsUndefined: true, trim: false })"
                                    @update:follow-up-message="tenantFollowUpMessage = $event"
                                    @request-follow-up="sendClientFollowUpRequest"
                                />
                            </template>
                        </div>
                    </div>
                </div>

                <SheetSectionNavigation
                    v-model="detailPanel"
                    :options="detailPanelOptions"
                    placement="bottom"
                    aria-label="Task detail sections"
                    class="shrink-0 border-t border-border/70 bg-background"
                />

                <SheetFooter class="shrink-0 border-t border-border/70 bg-background px-4 py-3 sm:px-6 sm:justify-between">
                    <p
                        class="text-xs font-medium"
                        :class="{
                            'text-muted-foreground': saveState === 'idle',
                            'text-sky-700': saveState === 'saving',
                            'text-emerald-700': saveState === 'saved',
                            'text-destructive': saveState === 'error',
                        }"
                    >
                        {{ saveStateLabel }}
                    </p>

                    <div class="flex flex-wrap items-center justify-end gap-2">
                        <TimerControl
                            v-if="canMutate"
                            :entry="timeEntriesStore.runningEntry"
                            :loading="timeEntriesStore.saving"
                            start-label="Track time"
                            @start="startTaskTimer"
                            @pause="pauseTaskTimer"
                            @resume="resumeTaskTimer"
                            @stop="stopTaskTimer"
                        />
                        <Button v-if="linkedProjectHref" variant="ghost" size="sm" as-child>
                            <NuxtLink :to="linkedProjectHref">Open project</NuxtLink>
                        </Button>
                        <Button variant="outline" size="sm" @click="handleOpenChange(false)">Close</Button>
                        <Button v-if="!isCompleted" size="sm" class="gap-1.5" @click="markTaskDone">
                            <CheckCircle2 class="size-4" />
                            Mark done
                        </Button>
                    </div>
                </SheetFooter>
            </div>
        </SheetContent>
    </Sheet>

    <ConfirmDialog
        v-model:open="deleteConfirmOpen"
        title="Delete task?"
        :description="`Delete ${task?.title || 'this task'} permanently? This cannot be undone.`"
        confirm-label="Delete task"
        :loading="deleting"
        destructive
        @confirm="handleDelete"
    />
</template>
